import { Controller } from "@hotwired/stimulus"
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Interfaces
interface LatLng {
  lat: number
  lng: number
}

export default class extends Controller {
  static values = {
    latitude: Number,
    longitude: Number,
    orientation: Number,
    fovAngle: { type: Number, default: 90 },
    viewDistance: { type: Number, default: 300 }
  }

  declare latitudeValue: number
  declare longitudeValue: number
  declare orientationValue: number
  declare fovAngleValue: number
  declare viewDistanceValue: number

  private map: L.Map | null = null
  private marker: L.Marker | null = null
  private fovCone: L.Polygon | null = null

  connect() {
    // Make sure lat/lng exists 
    if (!this.latitudeValue || !this.longitudeValue) {
      console.warn("Camera location not set")
      return
    }

    this.initializeMap()
  }

  disconnect() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  private initializeMap() {
    const position: LatLng = { lat: this.latitudeValue, lng: this.longitudeValue }

    // Initialize the map
    this.map = L.map(this.element as HTMLElement).setView([position.lat, position.lng], 16)
    
    // Add tiles with HTTPS
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19
    }).addTo(this.map)

    // Add camera marker
    this.marker = L.marker([position.lat, position.lng]).addTo(this.map)

    // Create FOV cone if orientation is provided
    if (this.orientationValue !== undefined) {
      this.createFovCone(position, this.orientationValue, this.fovAngleValue, this.viewDistanceValue)
    }
  }

  private createFovCone(position: LatLng, bearing: number, angle: number, distance: number) {
    if (!this.map) return;
    
    // Calculate the cone points based on camera position, direction and FOV angle
    const point1 = this.calculatePoint(position, bearing - angle/2, distance)
    const point2 = this.calculatePoint(position, bearing + angle/2, distance)
    
    // Create an SVG element for a radial gradient
    const gradientId = `fov-gradient-${Math.random().toString(36).substring(2, 9)}`
    const svgNS = 'http://www.w3.org/2000/svg'
    const svg = document.createElementNS(svgNS, 'svg')
    svg.setAttribute('style', 'position: absolute; width: 0; height: 0;')
    const defs = document.createElementNS(svgNS, 'defs')
    
    // Create a radial gradient definition
    const gradient = document.createElementNS(svgNS, 'radialGradient')
    gradient.setAttribute('id', gradientId)
    gradient.setAttribute('cx', '50%')
    gradient.setAttribute('cy', '50%')
    gradient.setAttribute('r', '100%')
    gradient.setAttribute('fx', '50%')
    gradient.setAttribute('fy', '50%')
    
    // Define gradient stops
    const stop1 = document.createElementNS(svgNS, 'stop')
    stop1.setAttribute('offset', '0%')
    stop1.setAttribute('style', 'stop-color: rgba(255, 0, 0, 0.4);')
    
    const stop2 = document.createElementNS(svgNS, 'stop')
    stop2.setAttribute('offset', '70%')
    stop2.setAttribute('style', 'stop-color: rgba(255, 0, 0, 0.15);')
    
    const stop3 = document.createElementNS(svgNS, 'stop')
    stop3.setAttribute('offset', '100%')
    stop3.setAttribute('style', 'stop-color: rgba(255, 0, 0, 0);')
    
    // Add stops to gradient
    gradient.appendChild(stop1)
    gradient.appendChild(stop2)
    gradient.appendChild(stop3)
    
    // Add gradient to defs and add defs to SVG
    defs.appendChild(gradient)
    svg.appendChild(defs)
    
    // Add SVG to document
    document.body.appendChild(svg)
    
    // Create polygon with camera position and the two calculated points
    this.fovCone = L.polygon([
      [position.lat, position.lng],
      [point1.lat, point1.lng],
      [point2.lat, point2.lng]
    ], {
      color: 'rgba(255, 0, 0, 0.6)',
      weight: 0,
      fillColor: 'none', // We'll use the gradient instead
      fillOpacity: 1,
      fill: true,
      className: `fov-cone-${gradientId}`
    }).addTo(this.map)
    
    // Add custom CSS for the gradient fill
    const style = document.createElement('style')
    style.innerHTML = `
      .fov-cone-${gradientId} {
        fill: url(#${gradientId}) !important;
      }
    `
    document.head.appendChild(style)
    
    // Clean up when the map is removed
    this.map.on('remove', () => {
      if (svg.parentNode) {
        svg.parentNode.removeChild(svg)
      }
      if (style.parentNode) {
        style.parentNode.removeChild(style)
      }
    })
  }

  private calculatePoint(position: LatLng, bearing: number, distance: number): LatLng {
    // Convert bearing to radians, note: 0° is North, 90° is East
    const bearingRad = (bearing * Math.PI) / 180
    
    // Earth's radius in meters
    const R = 6378137
    
    // Angular distance in radians
    const angularDistance = distance / R
    
    // Convert latitude and longitude to radians
    const latRad = (position.lat * Math.PI) / 180
    const lngRad = (position.lng * Math.PI) / 180
    
    // Calculate new latitude
    const newLatRad = Math.asin(
      Math.sin(latRad) * Math.cos(angularDistance) +
      Math.cos(latRad) * Math.sin(angularDistance) * Math.cos(bearingRad)
    )
    
    // Calculate new longitude
    const newLngRad = lngRad + Math.atan2(
      Math.sin(bearingRad) * Math.sin(angularDistance) * Math.cos(latRad),
      Math.cos(angularDistance) - Math.sin(latRad) * Math.sin(newLatRad)
    )
    
    // Convert back to degrees
    const newLat = (newLatRad * 180) / Math.PI
    const newLng = (newLngRad * 180) / Math.PI
    
    return { lat: newLat, lng: newLng }
  }
} 